import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
} from '@angular/common/http';
import localeIt from '@angular/common/locales/it';
import localeJa from '@angular/common/locales/ja';
import localeNb from '@angular/common/locales/nb';
import localePl from '@angular/common/locales/pl';
import localeZh from '@angular/common/locales/zh';
import { APP_INITIALIZER, NgModule, Optional, SkipSelf } from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
} from '@angular/material-moment-adapter';
import { NgMapsCoreModule } from '@ng-maps/core';
import { GOOGLE_MAPS_API_CONFIG } from '@ng-maps/google';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxPopperjsModule, NgxPopperjsPlacements } from 'ngx-popperjs';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  AuthHttpInterceptor,
  ErrorInterceptor,
  LoaderInterceptor,
} from './interceptor';
import { CoreRoutingModule } from './routing';
import { UserService } from './service/user.service';
import { Locale, PermissionType, Timezone } from '@model';
import {
  CookieService,
  CssService,
  CustomEventService,
  CustomGhgService,
  DashboardService,
  DialogService,
  EssService,
  ExcelService,
  ForecastService,
  GlobalAlertService,
  GoogleService,
  GridProgramService,
  I18nService,
  IconsService,
  InlineEditService,
  LoadProfileService,
  LoaderService,
  LocalesService,
  LoginRedirectService,
  NavbarService,
  PermissionService,
  ProjectService,
  ResourceService,
  ResultService,
  RouterService,
  ScenarioEventsService,
  ScenarioService,
  SidenavService,
  SiteConstraintService,
  SolarPvService,
  TariffService,
  TimezonesService,
  TreeService,
} from '@service';
import { BackendService } from './service/backend.service';

registerLocaleData(localeIt, 'it-IT');
registerLocaleData(localeZh, 'zh-TW');
registerLocaleData(localePl, 'pl-PL');
registerLocaleData(localeJa, 'ja-JP');
registerLocaleData(localeNb, 'nb-NO');

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

const initRouterCheck = (routerService: RouterService) => (): Promise<void> =>
  routerService.initRouterCheck();

const initGoogleApiKey = (google: GoogleService) => (): Observable<string> =>
  google.load();

const initIcon = (icon: IconsService) => (): Promise<void> => icon.load();

const initTimezones =
  (timezone: TimezonesService) => (): Observable<Timezone[]> =>
    timezone.loadTimezones();

const initLocales = (locale: LocalesService) => (): Observable<Locale[]> =>
  locale.setLocales();

const initLoginRedirect =
  (loginRedirect: LoginRedirectService) => (): Promise<void> =>
    loginRedirect.loadLoginPage();

const initNav = (navbarService: NavbarService) => (): Promise<object> =>
  navbarService.url();

const initPermissions =
  (permissionsService: PermissionService) => (): Observable<PermissionType> =>
    permissionsService.getPermissions().pipe(take(1));

const placement = NgxPopperjsPlacements.RIGHT;

@NgModule({
  imports: [
    CoreRoutingModule,
    NgMapsCoreModule,
    NgxPopperjsModule.forRoot({ placement }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    HttpClientModule,
  ],
  exports: [CoreRoutingModule, NgMapsCoreModule, TranslateModule],
  providers: [
    BackendService,
    CookieService,
    CssService,
    CustomEventService,
    CustomGhgService,
    DashboardService,
    DialogService,
    EssService,
    ExcelService,
    ForecastService,
    GlobalAlertService,
    GoogleService,
    GridProgramService,
    I18nService,
    IconsService,
    InlineEditService,
    TreeService,
    LoadProfileService,
    LoaderService,
    LocalesService,
    LoginRedirectService,
    ProjectService,
    ResourceService,
    ResultService,
    RouterService,
    ScenarioService,
    ScenarioEventsService,
    SidenavService,
    SolarPvService,
    SiteConstraintService,
    TariffService,
    TimezonesService,
    PermissionService,
    UserService,
    {
      provide: APP_INITIALIZER,
      useFactory: initLoginRedirect,
      deps: [LoginRedirectService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initNav,
      deps: [NavbarService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initIcon,
      deps: [IconsService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initLocales,
      deps: [LocalesService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initGoogleApiKey,
      deps: [GoogleService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initPermissions,
      deps: [PermissionService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initRouterCheck,
      deps: [RouterService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initTimezones,
      deps: [TimezonesService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    {
      provide: GOOGLE_MAPS_API_CONFIG,
      useClass: GoogleService,
    },
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: {
        useUtc: false,
        strict: true,
      },
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
})
export class CoreModule {
  // Make sure CoreModule is imported only by one NgModule the AppModule
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import only in AppModule');
    }
  }
}
